import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { StyledEngineProvider, CssVarsProvider } from '@mui/joy/styles/index.js'
import Box from '@mui/joy/Box/index.js'
import CssBaseline from '@mui/joy/CssBaseline/index.js'
import { Amplify } from 'aws-amplify'
import Card from '@mui/joy/Card/index.js'
import Typography from '@mui/joy/Typography/index.js'
import Divider from '@mui/joy/Divider/index.js'
import CardContent from '@mui/joy/CardContent/index.js'
import CardActions from '@mui/joy/CardActions/index.js'
import Button from '@mui/joy/Button/index.js'
import { signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth'
import MessagesPane from './components/MessagesPane.jsx'
import ConversationList from './components/ConversationList.jsx'
import AdminPanel from './components/AdminPanel.jsx'
import '@fontsource/inter'
import { get } from 'aws-amplify/api'

const SUPPORTED_MODELS = ['TEM8', 'TAM9', 'TAMX', 'TEM6', 'TEM4']

function App() {
  const [session, setSession] = useState(null)
  const [admin, setAdmin] = useState(false)
  const [showIntro, setShowIntro] = useState(false)
  const [conversationId, setConversationId] = React.useState(null)
  const [conversation, setConversation] = React.useState(null)
  const [currentUser, setCurrentUser] = React.useState(null)
  const [loggedInUser, setLoggedInUser] = React.useState(null)
  const [view, setView] = React.useState('chat')

  useEffect(() => {
    void (async () => {
      const s = await fetchAuthSession()
      if (!s.tokens) {
        await signInWithRedirect({
          provider: { custom: 'trane-residential-cognito' },
        })
      } else {
        const isAdmin =
          s.tokens.idToken?.payload?.['cognito:groups']?.includes(
            'TechAssistAdmin',
          ) || false

        const username = s?.tokens?.idToken?.payload['cognito:username']
        setLoggedInUser(username)

        if (isAdmin) {
          setAdmin(true)
        }
        setSession(s)
      }
    })()
  }, [])

  useEffect(() => {
    if (!conversationId) {
      return
    }

    void (async () => {
      const s = await fetchAuthSession()
      const idToken = s.tokens?.idToken?.toString()

      const path =
        currentUser?.username ?
          `/1/conversations/${conversationId}?username=${encodeURIComponent(currentUser.username)}`
        : `/1/conversations/${conversationId}`

      const restOperation = get({
        apiName: 'technician-bot',
        path,
        options: {
          headers: {
            Authorization: idToken,
          },
        },
      })

      const { body } = await restOperation.response
      const response = await body.json()

      if (!response) {
        setConversation({
          messages: [
            {
              sender: 'Tech Assistant',
              content: 'No conversation found.',
            },
          ],
        })
      } else {
        console.log('Conversation:', response)
        setConversation(response)
      }
    })()
  }, [conversationId, currentUser])

  return !session ? null : (
      <>
        {view === 'admin' ?
          <AdminPanel />
        : <Box
            sx={{
              display: 'flex',
              height: '100vh',
              width: '100vw',
            }}
          >
            <ConversationList
              onSelectConversation={(c) => {
                setConversationId(c)
              }}
              admin={admin}
              onSelectUser={(user) => {
                setCurrentUser(user)
                setConversation({
                  messages: [],
                })
              }}
            />
            <Box sx={{ flex: 1 }}>
              <MessagesPane
                conversation={conversation}
                isInputDisabled={
                  admin && currentUser && currentUser.username !== loggedInUser
                }
                admin={admin}
                view={view}
                onToggleView={() => {
                  setView(view === 'chat' ? 'admin' : 'chat')
                }}
              />
            </Box>
          </Box>
        }
        {showIntro && (
          <Card
            sx={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%',
              height: '650px',
              overflow: 'auto',
            }}
          >
            <Typography level="title-lg">
              Welcome to the Technician Bot Test Console
            </Typography>
            <Divider inset="none" />
            <CardContent>
              <Typography>
                This is an <strong>extremely</strong> early preview of the
                &quot;Guided Service&quot; technician bot. Currently, this bot
                has access to the product literature from most air handlers that
                are included in the product literature database.
              </Typography>
              <ul>
                {SUPPORTED_MODELS.map((handler, index) => (
                  <li key={index}>{handler}</li>
                ))}
              </ul>
              <Typography>
                More equipment will be added over the coming weeks as we start
                to work out the kinks.
              </Typography>
              <Typography>
                Additionally, there is a long list of things that do not work
                yet, but are coming soon, including:
              </Typography>
              <ul>
                <li>
                  Returning the manual pages where the information was sourced
                  from.
                </li>
                <li>
                  Returning an image or diagram where it would be more helpful
                  than text.
                </li>
              </ul>
            </CardContent>
            <CardActions>
              <Button
                onClick={() => {
                  setShowIntro(false)
                }}
              >
                Okay
              </Button>
            </CardActions>
          </Card>
        )}
      </>
    )
}

const getConfigUrl = () => {
  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  if (isLocalhost) {
    return '/amplify-config/trane/config.json'
  }
  return `https://technician-bot-api.${window.location.host.split('.').slice(-2).join('.')}/amplify-config/trane/config.json`
}

fetch(getConfigUrl())
  .then(async (response) => await response.json())
  .then(async (config) => {
    Amplify.configure(config)

    // Initialize your app
    const rootElement = document.getElementById('root')
    const root = createRoot(rootElement)
    root.render(
      <StyledEngineProvider injectFirst>
        <CssVarsProvider disableTransitionOnChange>
          <CssBaseline />
          <App />
        </CssVarsProvider>
      </StyledEngineProvider>,
    )
  })
  .catch((error) => {
    console.error('Error fetching configuration:', error)
  })
